import { useSnackbar } from 'notistack'
import React, { useEffect, useState } from 'react'
import { INTRANET_ROLES, INTRANET_ROLE_LABELS, IntranetRoleType } from '@/pages/Intranet/Companies'
import ApiService from '@/services/Api'
import { CloseOutlined as CloseIcon } from '@mui/icons-material'
import UserPermissionsEditor, {
    IUserPermissions,
    defaultPermissions
} from './UserPermissionsEditor'
import { IUser, IRadio } from './UserFormDialog'
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Typography
} from '@mui/material'
import { permissionFamilies, permissionTypes } from './UserPermissionsEditor'

const INTRANET_COMPANY_ROLES: IntranetRoleType[] = [...INTRANET_ROLES]

interface UserCompanyDialogProps {
    open: boolean
    onClose: () => void
    /** Usuário da linha da tabela, com `companyUuid`/`companyUuids` para definir a rádio */
    user: IUser | null
    /** Lista de rádios, usada para exibir os nomes no seletor de rádio */
    radios: IRadio[]
    /** Chamado após salvar com sucesso, para o pai atualizar a listagem */
    onSaved: () => void
}

// Faz o merge "profundo" das permissões vindas da API com o objeto default,
// garantindo que toda família/tipo tenha sempre um boolean definido
// (evita que campos ausentes no backend virem `undefined` no estado).
const mergePermissions = (
    apiPermissions: Partial<IUserPermissions> | undefined | null
): IUserPermissions => {
    const merged = {} as IUserPermissions
    permissionFamilies.forEach(family => {
        merged[family] = {
            ...defaultPermissions[family],
            ...(apiPermissions?.[family] || {})
        }
    })
    return merged
}

const UserCompanyDialog: React.FC<UserCompanyDialogProps> = ({
    open,
    onClose,
    user,
    radios,
    onSaved
}) => {
    const { enqueueSnackbar } = useSnackbar()

    const [companyUuid, setCompanyUuid] = useState<string>('')
    const [companyName, setCompanyName] = useState<string>('')
    const [role, setRole] = useState<string>('')
    const [permissions, setPermissions] = useState<IUserPermissions>(defaultPermissions)
    const [isLoading, setIsLoading] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [companyUsers, setCompanyUsers] = useState<any[]>([])
    const [confirmAllFalseOpen, setConfirmAllFalseOpen] = useState(false)
    // Evita "ghost click" em telas touch: o mesmo toque que abre o modal de
    // confirmação (empilhado sobre o modal principal, com botões quase na
    // mesma posição de tela) pode "vazar" e acionar o botão "Confirmar"
    // imediatamente. Mantemos o botão desabilitado por um instante ao abrir.
    const [confirmButtonReady, setConfirmButtonReady] = useState(false)

    useEffect(() => {
        if (!confirmAllFalseOpen) {
            setConfirmButtonReady(false)
            return
        }
        const timer = setTimeout(() => setConfirmButtonReady(true), 350)
        return () => clearTimeout(timer)
    }, [confirmAllFalseOpen])

    // (Re)initializes a rádio selecionada sempre que o modal é aberto
    useEffect(() => {
        if (!open || !user) return
        setCompanyUuid(user.companyUuid || user.companyUuids[0] || '')
        setCompanyName('')
        setRole('')
        setPermissions(defaultPermissions)
        setCompanyUsers([])
    }, [open, user])

    // Carrega role/permissions da rádio selecionada ao abrir ou trocar de rádio
    useEffect(() => {
        if (!open || !user || !companyUuid) return

        let cancelled = false
        const loadCompanyUser = async () => {
            setIsLoading(true)
            try {
                const { data } = await ApiService.get(`/user/list?companyUuid=${companyUuid}`)
                if (cancelled) return

                const items = data || []
                const item = items.find((u: any) => u.uuid === user.uuid)
                const company = item?.company

                setCompanyName(
                    company?.name ||
                    radios.find(r => r.uuid === companyUuid)?.name ||
                    companyUuid
                )
                setRole(company?.role || '')
                setPermissions(mergePermissions(company?.permissions))
                setCompanyUsers(items)
            } catch (error: any) {
                if (cancelled) return
                enqueueSnackbar(
                    error?.data ||
                    'Ocorreu um erro ao carregar os dados do usuário na rádio.',
                    { variant: 'error' }
                )
            } finally {
                if (!cancelled) setIsLoading(false)
            }
        }

        loadCompanyUser()
        return () => {
            cancelled = true
        }
    }, [open, user, companyUuid, radios, enqueueSnackbar])

    // Regras do user_company: apenas o administrador pode ter permissões de Usuários
    useEffect(() => {
        setPermissions(prev => {
            const isAdministrator = role === 'administrator'
            const hasAllUsersPermissions = Object.values(prev.users).every(Boolean)
            const hasAnyUsersPermission = Object.values(prev.users).some(Boolean)

            if (isAdministrator) {
                if (hasAllUsersPermissions) return prev
                return { ...prev, users: { read: true, write: true, delete: true, update: true } }
            }

            if (!hasAnyUsersPermission) return prev
            return { ...prev, users: { read: false, write: false, delete: false, update: false } }
        })
    }, [role])

    const companyOptions = user?.companyUuids?.length
        ? user.companyUuids
        : user?.companyUuid
            ? [user.companyUuid]
            : []

    // Usa checagem "falsy" (!valor) em vez de `=== false` para não deixar
    // passar despercebido nenhum valor `undefined`/`null` vindo da API.
    const areAllPermissionsFalse = (perms: IUserPermissions): boolean =>
        permissionFamilies.every(family =>
            permissionTypes.every(type => !perms[family][type])
        )

    const savePermissions = async () => {
        console.log('[DEBUG savePermissions] chamado com role:', role, 'permissions:', JSON.stringify(permissions))
        if (!user || !companyUuid || !role) return

        setIsSaving(true)
        try {
            await ApiService.put(`/user/${user.uuid}/company/${companyUuid}`, {
                role,
                permissions
            })
            enqueueSnackbar('Função e permissões atualizadas com sucesso.', {
                variant: 'success'
            })
            onSaved()
            onClose()
        } catch (error: any) {
            enqueueSnackbar(
                error?.data ||
                'Ocorreu um erro ao atualizar a função do usuário na rádio.',
                { variant: 'error' }
            )
        } finally {
            setIsSaving(false)
        }
    }

    const handleSave = async () => {
        console.log('[DEBUG handleSave] user:', user?.uuid, 'companyUuid:', companyUuid, 'role:', role)
        if (!user || !companyUuid || !role) {
            console.log('[DEBUG handleSave] saiu cedo: faltando user/companyUuid/role')
            return
        }

        if (
            role === 'administrator' &&
            companyUsers.some(u => u.uuid !== user.uuid && u.company?.role === 'administrator')
        ) {
            console.log('[DEBUG handleSave] bloqueado: já existe administrador')
            enqueueSnackbar(
                'Só é permitido um administrador por rádio. Essa rádio já possui um administrador.',
                { variant: 'error' }
            )
            return
        }

        const allFalse = areAllPermissionsFalse(permissions)
        console.log('[DEBUG handleSave] permissions:', JSON.stringify(permissions))
        console.log('[DEBUG handleSave] areAllPermissionsFalse =', allFalse)

        if (allFalse) {
            console.log('[DEBUG handleSave] abrindo modal de confirmação')
            setConfirmAllFalseOpen(true)
            return
        }

        console.log('[DEBUG handleSave] NÃO é tudo falso -> salvando direto')
        await savePermissions()
    }

    const confirmAllFalseSave = async () => {
        console.log('[DEBUG confirmAllFalseSave] chamado (usuário clicou em Confirmar)')
        // Guarda extra: ignora cliques que cheguem antes do botão estar
        // realmente pronto (proteção redundante ao `disabled` do botão).
        if (!confirmButtonReady) {
            console.log('[DEBUG confirmAllFalseSave] ignorado: confirmButtonReady = false')
            return
        }
        setConfirmAllFalseOpen(false)
        await savePermissions()
    }

    return (
        <>
        <Dialog
            open={open}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { borderRadius: '12px' }
            }}
        >
            <DialogTitle sx={{ pr: 6, position: 'relative' }}>
                Função na rádio
                <IconButton
                    aria-label="Fechar"
                    onClick={onClose}
                    disabled={isSaving}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: 'text.secondary'
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                {!user ? (
                    <Grid container justifyContent="center" sx={{ py: 6 }}>
                        <CircularProgress size={60} color="primary" />
                    </Grid>
                ) : (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="h6">{user.name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {user.email}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mt: 0.5 }}
                            >
                                Rádio: {companyName || companyUuid}
                            </Typography>
                        </Grid>

                        {companyOptions.length > 1 && (
                            <Grid item xs={12}>
                                <FormControl fullWidth variant="outlined" size="small">
                                    <InputLabel id="company_radio_select_label">Rádio</InputLabel>
                                    <Select
                                        id="company_radio_select_field"
                                        labelId="company_radio_select_label"
                                        label="Rádio"
                                        value={companyUuid}
                                        onChange={e => setCompanyUuid(e.target.value as string)}
                                        disabled={isLoading || isSaving}
                                    >
                                        {companyOptions.map(uuid => (
                                            <MenuItem key={uuid} value={uuid}>
                                                {radios.find(r => r.uuid === uuid)?.name || uuid}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <FormControl fullWidth variant="outlined" size="small">
                                <InputLabel id="company_role_select_label">Função na rádio</InputLabel>
                                <Select
                                    id="company_role_select_field"
                                    labelId="company_role_select_label"
                                    label="Função na rádio"
                                    value={role}
                                    onChange={e => setRole(e.target.value as string)}
                                    disabled={isLoading || isSaving}
                                >
                                    {INTRANET_COMPANY_ROLES.map(roleOption => (
                                        <MenuItem key={roleOption} value={roleOption}>
                                            {INTRANET_ROLE_LABELS[roleOption]}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {isLoading ? (
                            <Grid item xs={12}>
                                <Grid container justifyContent="center" sx={{ py: 4 }}>
                                    <CircularProgress size={28} color="primary" />
                                </Grid>
                            </Grid>
                        ) : (
                            <>
                                <Grid item xs={12}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            display: 'block',
                                            fontWeight: 700,
                                            letterSpacing: '0.08em',
                                            textTransform: 'uppercase',
                                            color: 'text.secondary',
                                            mt: 1
                                        }}
                                    >
                                        Permissões
                                    </Typography>
                                </Grid>
                                <UserPermissionsEditor
                                    permissions={permissions}
                                    onChange={setPermissions}
                                    disabledFamilies={role === 'administrator' ? [] : ['users']}
                                />
                            </>
                        )}
                    </Grid>
                )}
            </DialogContent>

            <Divider />

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} color="primary" disabled={isSaving}>
                    Cancelar
                </Button>
                <Button
                    onClick={handleSave}
                    color="primary"
                    variant="contained"
                    disabled={isLoading || isSaving}
                    endIcon={isSaving && <CircularProgress size={15} color="inherit" />}
                >
                    Salvar
                </Button>
            </DialogActions>
        </Dialog>

        <Dialog
            open={confirmAllFalseOpen}
            onClose={() => setConfirmAllFalseOpen(false)}
            maxWidth="sm"
            PaperProps={{ sx: { borderRadius: '12px' } }}
        >
            <DialogTitle>Atenção</DialogTitle>
            <DialogContent>
                <Typography>
                    Tem certeza que deseja salvar todas as permissões desse usuário para a rádio{' '}
                    <strong>{companyName || companyUuid}</strong> como falsas? Essa ação resulta na
                    perda do acesso do usuário aos dados do Metrics.
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={() => setConfirmAllFalseOpen(false)} color="primary">
                    Cancelar
                </Button>
                <Button
                    onClick={confirmAllFalseSave}
                    color="primary"
                    variant="contained"
                    disabled={isSaving || !confirmButtonReady}
                    endIcon={isSaving && <CircularProgress size={15} color="inherit" />}
                >
                    Confirmar
                </Button>
            </DialogActions>
        </Dialog>
        </>
    )
}

export default UserCompanyDialog
